import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Camera, Download, Images, Info, Share2, ShieldCheck, Wand2 } from "lucide-react";

import { api, downloadDataUrl } from "@/lib/api";
import { pickFromFiles } from "@/lib/media";
import { StyleMiniCard } from "@/components/cards";
import { Bullets, Button, Card, CheckRow, Chip, ScreenHeader } from "@/components/kit";
import { Page } from "@/components/layout";
import { useCamera } from "@/components/widgets";
import { CameraBlocked } from "@/pages/Scan";
import { useApp } from "@/store";

const STEPS = ["Reading your photo…", "Mapping the hairline…", "Styling the new cut…", "Matching your lighting…", "Final touches…"];
const DISCLAIMER = "AI-generated preview — actual results may vary.";

export default function TryOn() {
  const [params] = useSearchParams();
  const { recs, profile, setTryOn, scanPhoto } = useApp();
  const navigate = useNavigate();
  const [openCamera, cameraEl] = useCamera();
  const [consent, setConsent] = useState(false);
  const [photo, setPhoto] = useState(params.get("fromScan") && scanPhoto ? scanPhoto : null);
  const [pickError, setPickError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [all, setAll] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [slug, setSlug] = useState(params.get("slug") || undefined);
  const [phase, setPhase] = useState("setup");
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("preview");

  const top = (recs ?? []).map((r) => r.style);
  const pool = showAll ? all ?? [] : top;
  const selected = [...top, ...(all ?? [])].find((s) => s.slug === slug);

  useEffect(() => {
    api("/hairstyles").then(setAll).catch(() => setAll([]));
  }, []);
  useEffect(() => {
    if (!slug && top[0]) setSlug(top[0].slug);
  }, [slug, top]);
  useEffect(() => {
    if (slug && all && !top.find((s) => s.slug === slug)) setShowAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all]);
  useEffect(() => {
    if (phase !== "generating") return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const choose = async (src) => {
    setBlocked(false);
    setPickError(null);
    const r = src === "camera" ? await openCamera(true) : await pickFromFiles();
    if (r.status === "blocked") setBlocked(true);
    if (r.status === "error") setPickError(r.message);
    if (r.status === "picked") setPhoto({ uri: r.uri, base64: r.base64 });
  };

  const generate = async () => {
    if (!photo || !slug) return;
    setError(null);
    setResult(null);
    setPhase("generating");
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const r = await api("/tryon", { method: "POST", body: { image: photo.base64, style_slug: slug, consent: true, hair_type: profile.hair_type ?? null }, timeoutMs: 120000 });
      setResult(r.image);
      setTryOn({ slug, image: r.image });
      setView("preview");
      setPhase("result");
    } catch (e) {
      setError(e.message);
      setPhase("error");
    }
  };

  const progress = Math.min(0.95, elapsed / 25);
  return (
    <div data-testid="tryon-screen">
      {cameraEl}
      <ScreenHeader testId="tryon-back" label="AI virtual try-on" />
      <Page className="flex flex-col gap-4 pb-12">
        <h1 data-testid="tryon-title" className="t-h1">TRY YOUR NEXT LOOK.</h1>

        {phase === "generating" ? (
          <div data-testid="tryon-generating" className="animate-fade-in flex flex-col items-center rounded-[20px] bg-navy p-6">
            {photo ? <img src={photo.uri} alt="Your selfie" className="mb-4 h-[190px] w-[150px] rounded-[20px] border-2 border-ocean object-cover" /> : null}
            <p className="text-center font-body-bold text-[11px] tracking-[1.4px] text-sky">GEMINI NANO BANANA · {selected?.name?.toUpperCase()}</p>
            <p data-testid="tryon-progress-step" aria-live="polite" className="mt-2 text-center font-display text-2xl text-white">{STEPS[Math.min(STEPS.length - 1, Math.floor(elapsed / 5))]}</p>
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded bg-[rgba(255,255,255,0.45)]"><div className="h-1.5 rounded bg-ocean transition-all duration-1000" style={{ width: `${progress * 100}%` }} /></div>
            <p data-testid="tryon-elapsed" className="mt-2 font-body-medium text-xs text-soft">{elapsed}s · usually 10–30 seconds</p>
          </div>
        ) : null}

        {phase === "result" && result && photo ? (
          <div data-testid="tryon-result" className="animate-fade-in flex flex-col gap-3">
            <div className="flex flex-wrap gap-2" role="tablist">
              {["preview", "original", "side"].map((v) => (
                <Chip key={v} testId={`tryon-view-${v}`} label={v === "side" ? "Side by side" : v === "preview" ? "Preview" : "Original"} selected={view === v} onClick={() => setView(v)} />
              ))}
            </div>
            {view === "side" ? (
              <div className="grid grid-cols-2 gap-2">
                <figure><img data-testid="tryon-original-image" src={photo.uri} alt="Your original" className="aspect-[3/4] w-full rounded-xl bg-tert object-cover" /><figcaption className="t-caption mt-1 text-center">Original</figcaption></figure>
                <figure><img data-testid="tryon-preview-image" src={result} alt="AI preview" className="aspect-[3/4] w-full rounded-xl bg-tert object-cover" /><figcaption className="t-caption mt-1 text-center">AI preview</figcaption></figure>
              </div>
            ) : (
              <div className="relative mx-auto aspect-[3/4] w-full max-w-md overflow-hidden rounded-[20px] bg-tert">
                <img data-testid={view === "preview" ? "tryon-preview-image" : "tryon-original-image"} src={view === "preview" ? result : photo.uri} alt={view === "preview" ? "AI preview" : "Your original"} className="h-full w-full object-cover" />
                <span className="absolute bottom-3 left-3 rounded-full bg-glass-dark px-3 py-1.5 font-body-bold text-[11px] tracking-[1px] text-white">{view === "preview" ? `AI PREVIEW · ${selected?.name?.toUpperCase()}` : "YOUR ORIGINAL"}</span>
              </div>
            )}
            <div data-testid="tryon-disclaimer" className="flex items-center gap-2 rounded-xl bg-tert p-3">
              <Info className="h-[18px] w-[18px] text-navy" aria-hidden /><p className="t-strong flex-1 !text-navy">{DISCLAIMER}</p>
            </div>
            <div className="flex gap-3">
              <Button testId="tryon-save-device" title="Download" icon={Download} variant="outline" onClick={() => downloadDataUrl(result, `breez-preview-${slug}.${result.startsWith("data:image/png") ? "png" : "jpg"}`)} className="flex-1" />
              <Button testId="tryon-share" title="Share" icon={Share2} onClick={() => navigate(`/share?slug=${slug}`)} className="flex-1" />
            </div>
            <Button testId="tryon-view-style" title="See style & BREEZ match" variant="secondary" onClick={() => navigate(`/style/${slug}`)} />
            <Button testId="tryon-another" title="Try another style" variant="ghost" onClick={() => setPhase("setup")} />
          </div>
        ) : null}

        {phase === "error" ? (
          <Card testId="tryon-error-card" className="flex flex-col gap-3 !border-[#EF4444]">
            <div className="flex items-center gap-2" role="alert"><AlertCircle className="h-5 w-5 text-[#EF4444]" /><p className="t-title !text-[#EF4444]">Preview couldn&apos;t be created</p></div>
            <p data-testid="tryon-error-message" className="t-body">{error}</p>
            {selected ? (
              <div className="flex items-center gap-3">
                <img data-testid="tryon-error-reference" src={selected.image_url} alt={selected.name} className="h-[90px] w-[72px] rounded-xl bg-tert object-cover" />
                <p className="t-caption flex-1">Reference image of the {selected.name} — this is a model photo, not your preview.</p>
              </div>
            ) : null}
            <div className="flex gap-3">
              <Button testId="tryon-retry" title="Try again" onClick={generate} className="flex-1" />
              <Button testId="tryon-error-back" title="Change" variant="outline" onClick={() => setPhase("setup")} className="flex-1" />
            </div>
          </Card>
        ) : null}

        {phase === "setup" ? (
          <>
            <Card testId="tryon-consent-card" className="!border-sky !bg-tert">
              <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-navy" aria-hidden /><h2 className="t-title">Before you upload</h2></div>
              <Bullets items={[
                "Your selfie is sent to Google Gemini only to create this preview.",
                "It isn't stored by BREEZ by default — the selfie and preview stay in this browser unless you download them.",
                "It isn't used to identify you, and isn't used for training.",
              ]} />
              <div className="mt-4"><CheckRow testId="tryon-consent-checkbox" checked={consent} onChange={setConsent} label="I understand and want to create a preview." /></div>
            </Card>
            {consent ? (
              <>
                {photo ? (
                  <div className="flex items-center gap-4">
                    <img data-testid="tryon-selfie" src={photo.uri} alt="Your selfie" className="h-[120px] w-24 rounded-xl bg-tert object-cover" />
                    <div className="flex flex-1 flex-col gap-2">
                      <p className="t-strong">Selfie ready</p>
                      {photo === scanPhoto ? <p data-testid="tryon-using-scan-selfie" className="t-caption">Using your scan selfie — kept in this browser&apos;s memory only.</p> : null}
                      <Button testId="tryon-change-photo" title="Change" variant="outline" onClick={() => setPhoto(null)} className="md:self-start" />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="t-body">We&apos;ll ask for camera access only if you take a selfie.</p>
                    <Button testId="tryon-take-selfie" title="Take selfie" icon={Camera} onClick={() => choose("camera")} />
                    <Button testId="tryon-upload-photo" title="Upload photo" icon={Images} variant="outline" onClick={() => choose("library")} />
                    {pickError ? <p data-testid="tryon-pick-error" role="alert" className="t-caption !text-[#EF4444]">{pickError}</p> : null}
                    {blocked ? <CameraBlocked testId="tryon-permission-blocked" /> : null}
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="t-h2">Pick a style</h2>
                    <div className="flex gap-2">
                      <Chip testId="tryon-show-top" label="Top 5" selected={!showAll} onClick={() => setShowAll(false)} />
                      <Chip testId="tryon-show-all" label="All 40" selected={showAll} onClick={() => setShowAll(true)} />
                    </div>
                  </div>
                  {!showAll && !top.length ? <p className="t-caption">Add your hair profile for a personal top 5, or browse all 40.</p> : null}
                  <div className="no-scrollbar -mx-5 flex gap-3 overflow-x-auto px-5 py-1 md:mx-0 md:px-0">
                    {pool.map((s) => (
                      <div key={s.slug} className={`shrink-0 rounded-[23px] border-[3px] ${slug === s.slug ? "border-ocean" : "border-transparent"}`}>
                        <StyleMiniCard testId={`tryon-style-${s.slug}`} style={s} width={130} onClick={() => setSlug(s.slug)} />
                      </div>
                    ))}
                  </div>
                  {selected ? <p data-testid="tryon-selected-style" className="t-strong">Selected: {selected.name}</p> : null}
                </div>
                <Button testId="tryon-generate" title="Generate preview" icon={Wand2} disabled={!photo || !slug} onClick={generate} />
              </>
            ) : null}
          </>
        ) : null}
      </Page>
    </div>
  );
}
